/**
 * Policy check (§8): capabilityOf, effective policy (project ⊕ area ⊕ agent), constraints, the default presets and
 * the MCP tool list. `decide` and `toolsFor` share these functions, so the tools offered and what decide accepts
 * never drift apart (§8.6). Item capabilities and the `level` constraint: decision 0037.
 */
import { GraphTools } from "@minga/protocol"
import { makesReady } from "./pages"
import { agentOf, areaOf, itemOf, kpiOf, leadOf, liveWorkerRuns, must, runOf } from "./state"
import { depthOf, topItemOf, workDoneIn } from "./tree"
import type {
  Actor,
  Agent,
  AgentId,
  Approver,
  AskedReason,
  Capability,
  GraphCommand,
  Ids,
  Item,
  ItemId,
  Policy,
  PolicyDocument,
  PolicyRule,
  PolicyStamp,
  ProjectState,
  RuleConstraints,
  Taint,
  UserId,
} from "./types"
import { nowCount, openPlannableCount } from "./validate"

export type PolicyVerdict =
  | { readonly _tag: "exempt"; readonly stamp: PolicyStamp }
  | { readonly _tag: "allow"; readonly stamp: PolicyStamp }
  | {
      readonly _tag: "propose"
      readonly stamp: PolicyStamp
      readonly approver: Approver
      readonly why: string
      /** Wave B2 (decision 0057): the rule that sends it to a person, stored on the card apart from the reasoning. */
      readonly asked: AskedReason
    }
  | { readonly _tag: "never"; readonly rule: string }

/** Forced to `never` for every agent and worker (structural, §8.1 step 3). */
export const FORCED_NEVER: ReadonlyArray<Capability> = ["edit_policy", "grant_integration", "outward_action"]

const rule = (capability: Capability, when?: RuleConstraints): PolicyRule =>
  when ? { capability, when } : { capability }

/**
 * Default policy presets (§8.4, decision 0037): exactly what `fromV1PolicyDocument` makes of the v1 presets (a test
 * asserts it), so old and new projects behave the same. People edit the resulting records; these are starting points.
 *
 * Rev 2.1 (decision 0018, "no limits at first"): the lead is permissive. Its plan changes (goals, items and their
 * checks, assignments, dependencies, spawning workers) are `allow` and apply at once, with no worker cap. Scheduled
 * work follows standing delegation when unmentioned; explicit proposal/never rules still apply. Org changes stay `propose`.
 */
export const PolicyPresets = {
  /** Project layer: says nothing except the forced nevers, so agent presets decide and unmentioned → propose. */
  project: { allow: [], propose: [], never: [...FORCED_NEVER] },
  lead: {
    allow: [
      rule("create_goal"),
      rule("update_goal"),
      rule("create_item", { level: "top" }),
      rule("update_item", { level: "top" }),
      rule("complete_item", { level: "top" }),
      rule("assign", { level: "top" }),
      rule("start_item"),
      rule("cancel_item", { level: "top" }),
      rule("create_item", { level: "child" }),
      rule("update_item", { level: "child" }),
      rule("revise_checks"),
      rule("cancel_item", { level: "child" }),
      rule("complete_item", { level: "child" }),
      rule("reprioritize"),
      rule("add_dependency"),
      rule("remove_dependency"),
      rule("spawn_workers"),
      rule("assign", { level: "child" }),
      rule("propose_memory"),
      rule("curate_memory"),
      // decision 0084 (§1.5): the Ready gate; the lead moves its own plans to Ready unless a person says otherwise
      rule("ready_item"),
      // decision 0133 §1 (0134): routine team matters are the lead's judgement within its mandate. What stays a
      // person's is forced elsewhere: another keeper's agent (cross_owner), an agent's level, the overview (pages)
      rule("change_kpi_target"),
      rule("define_kpi"),
      rule("hire_agent"),
      rule("create_area"),
      rule("update_area"),
      rule("update_agent"),
    ],
    propose: [],
    never: [...FORCED_NEVER],
  },
  role: {
    allow: [
      rule("create_item", { under: "own_area", level: "child" }),
      rule("update_item", { under: "own_item", level: "child" }),
      rule("cancel_item", { under: "own_item", level: "child" }),
      rule("complete_item", { under: "own_item", level: "child" }),
      rule("add_dependency", { under: "own_area" }),
      rule("reprioritize", { under: "own_area" }),
      rule("spawn_workers"),
      rule("propose_memory"),
      // decision 0084 (§1.5): mirrors its create rules
      rule("ready_item", { under: "own_area", level: "child" }),
      // decision 0134 (0133 §1): an area's agent defines its own area's KPIs; elsewhere it proposes them (a card)
      rule("define_kpi", { under: "own_area" }),
    ],
    propose: [
      rule("create_item", { level: "top" }),
      rule("ready_item", { level: "top" }),
      rule("update_item", { level: "top" }),
      rule("complete_item", { level: "top" }),
      rule("assign", { level: "top" }),
      rule("start_item"),
      rule("cancel_item", { level: "top" }),
      rule("assign", { level: "child" }),
      rule("revise_checks"),
    ],
    never: [
      "create_goal",
      "change_kpi_target",
      "hire_agent",
      "create_area",
      "update_area",
      "update_agent",
      "curate_memory",
      ...FORCED_NEVER,
    ],
  },
  reviewer: {
    allow: [rule("propose_memory")],
    propose: [],
    never: [
      "create_goal",
      "update_goal",
      "change_kpi_target",
      "define_kpi",
      "create_area",
      "update_area",
      "hire_agent",
      "update_agent",
      "create_item",
      "update_item",
      "complete_item",
      "assign",
      "start_item",
      "cancel_item",
      "revise_checks",
      "reprioritize",
      "add_dependency",
      "remove_dependency",
      "spawn_workers",
      "curate_memory",
      ...FORCED_NEVER,
    ],
  },
} as const satisfies Record<string, PolicyDocument>

/** Is the item top-level before or after a move (the stricter: a move touching the top level is a top-level move)? */
const moveTouchesTop = (t: Item | undefined, c: Extract<GraphCommand, { _tag: "MoveItem" }>): boolean => {
  const before = t ? t.parentId === null : false
  const after = c.parentId === undefined ? before : c.parentId === null
  return before || after
}

/** The capability a graph command exercises (§5.3, decision 0037). */
export const capabilityOf = (command: GraphCommand, state?: ProjectState): Capability => {
  switch (command._tag) {
    case "CreateGoal":
      return "create_goal"
    case "UpdateGoal":
    case "SetGoalStatus":
      return "update_goal"
    case "ChangeTarget":
      return "change_kpi_target"
    case "DefineKpi":
    case "UpdateKpi":
      return "define_kpi"
    case "CreateArea":
      return "create_area"
    case "UpdateArea":
    case "ReviseAreaCharter":
    case "ArchiveArea":
      return "update_area"
    case "HireAgent":
      return "hire_agent"
    case "UpdateAgent":
    case "ReviseAgentCharter":
    case "ChangeAgentMind":
    case "SetAgentStatus":
    case "TakeOverAgent":
      return "update_agent"
    case "CreateItem":
      return "create_item"
    case "UpdateItem":
      return command.patch.recurrence !== undefined ? "schedule_work" : "update_item"
    case "MoveItem": {
      // v1 StartInitiative and PauseInitiative were both start_initiative: a top-level move into or out of Now
      const t = state ? itemOf(state, command.itemId) : undefined
      const intoNow = command.status === "in_progress" && t?.status !== "in_progress"
      const outOfNow =
        t?.status === "in_progress" && command.status !== undefined && command.status !== "in_progress"
      return moveTouchesTop(t, command) && (intoNow || outOfNow) ? "start_item" : "update_item"
    }
    case "AssignItem":
      return "assign"
    case "ReviseChecks":
      return "revise_checks"
    case "CloseItem":
      return command.as === "done" ? "complete_item" : "cancel_item"
    case "RunOccurrence":
      return "schedule_work"
    case "DecideRequest":
    case "LegacyChange":
      // DecideRequest is decided before the capability lookup (the receiving area's agent); LegacyChange is refused
      return "update_item"
    case "Reprioritize":
      return "reprioritize"
    case "AddEdge":
      return "add_dependency"
    case "RemoveEdge":
      return "remove_dependency"
    case "ReviseBrief":
      return "revise_brief"
  }
}

/** What a graph command is about, for area-level policy and constraints. */
export interface Target {
  readonly areaId: Ids["AreaId"] | null
  /** For a cross-area request: the requesting side's area (own_area is checked there). */
  readonly requestingAreaId: Ids["AreaId"] | null
  /** The item the command is about; for CreateItem the parent (own_item = the parent's owner). */
  readonly itemId: ItemId | null
  /**
   * `itemId` has a parent. `own_item` holds only on such items (v1 `own_task` named a task, never an initiative; a
   * top-level item's own rule is `own_tree`).
   */
  readonly itemIsChild: boolean
  /** The top-level item of its tree (own_tree, ifWorkDone). */
  readonly topItemId: ItemId | null
  /** The target item is (will be) top-level; null = no item target (`level` never holds then). */
  readonly topLevel: boolean | null
  /**
   * The agent `to` checks (v1 parity, decision 0037): a CreateItem's explicit owner, an AssignItem's new owner of a
   * child item, a child item's owner; none for top-level items (v1 initiative commands named no assignee, and v1
   * CreateTask checked its explicit assignee) and for Reprioritize.
   */
  readonly toAgentId: AgentId | null
  /** Depth below the top-level item of the (new) item. */
  readonly depth: number | null
}

const NONE: Target = {
  areaId: null,
  requestingAreaId: null,
  itemId: null,
  itemIsChild: false,
  topItemId: null,
  topLevel: null,
  toAgentId: null,
  depth: null,
}

const ownerAgentIdOf = (t: Pick<Item, "owner"> | undefined): AgentId | null =>
  t?.owner?._tag === "agent" ? t.owner.agentId : null

const itemTarget = (s: ProjectState, id: ItemId): Target => {
  const t = itemOf(s, id)
  if (!t) return { ...NONE, itemId: id }
  const child = t.parentId !== null
  return {
    areaId: t.areaId,
    requestingAreaId: t.areaId,
    itemId: t.id,
    itemIsChild: child,
    topItemId: topItemOf(s, t).id,
    topLevel: !child,
    toAgentId: child ? ownerAgentIdOf(t) : null,
    depth: depthOf(s, t),
  }
}

export const targetOf = (s: ProjectState, c: GraphCommand): Target => {
  switch (c._tag) {
    case "CreateItem": {
      const parent = c.parentId ? itemOf(s, c.parentId) : undefined
      const homeArea = parent ? parent.areaId : (c.areaId ?? null)
      const areaId = c.areaId ?? homeArea
      return {
        areaId,
        requestingAreaId: homeArea,
        itemId: parent?.id ?? null,
        itemIsChild: parent ? parent.parentId !== null : false,
        topItemId: parent ? topItemOf(s, parent).id : null,
        topLevel: !c.parentId,
        toAgentId: c.owner?._tag === "agent" ? c.owner.agentId : null,
        depth: parent ? depthOf(s, parent) + 1 : 0,
      }
    }
    case "MoveItem": {
      const base = itemTarget(s, c.itemId)
      const t = itemOf(s, c.itemId)
      return { ...base, topLevel: moveTouchesTop(t, c) }
    }
    case "AssignItem": {
      const base = itemTarget(s, c.itemId)
      return c.owner === undefined || !base.itemIsChild
        ? base
        : { ...base, toAgentId: c.owner?._tag === "agent" ? c.owner.agentId : null }
    }
    case "UpdateItem":
    case "ReviseChecks":
    case "CloseItem":
    case "RunOccurrence":
    case "DecideRequest":
      return itemTarget(s, c.itemId)
    case "Reprioritize": {
      const ref = c.ref
      if (ref._tag === "goal") return NONE
      return { ...itemTarget(s, ref.id), toAgentId: null }
    }
    case "AddEdge":
      return {
        ...NONE,
        areaId: itemOf(s, c.to.id)?.areaId ?? null,
        requestingAreaId: itemOf(s, c.from.id)?.areaId ?? null,
      }
    case "RemoveEdge": {
      const e = s.edges.find((x) => x.id === c.edgeId)
      return {
        ...NONE,
        areaId: e ? (itemOf(s, e.to.id)?.areaId ?? null) : null,
        requestingAreaId: e ? (itemOf(s, e.from.id)?.areaId ?? null) : null,
      }
    }
    case "CreateArea":
    case "UpdateArea":
    case "ReviseAreaCharter":
    case "ArchiveArea":
      return { ...NONE, areaId: c.areaId, requestingAreaId: c.areaId }
    case "HireAgent":
      return { ...NONE, areaId: c.areaId, requestingAreaId: c.areaId }
    case "UpdateAgent":
    case "ReviseAgentCharter":
    case "ChangeAgentMind":
    case "SetAgentStatus":
    case "TakeOverAgent": {
      const a = agentOf(s, c.agentId)?.areaId ?? null
      return { ...NONE, areaId: a, requestingAreaId: a }
    }
    case "DefineKpi":
      return { ...NONE, areaId: c.areaId ?? null, requestingAreaId: c.areaId ?? null }
    case "UpdateKpi": {
      // decision 0134: an area's agent keeps its own area's KPIs (own_area)
      const a = kpiOf(s, c.kpiId)?.areaId ?? null
      return { ...NONE, areaId: a, requestingAreaId: a }
    }
    default:
      return NONE
  }
}

/** Constraint evaluation (§8.3), against the state the change is decided on: deterministic and replayable. */
export const constraintsHold = (
  s: ProjectState,
  actor: Actor,
  agent: Agent,
  command: GraphCommand,
  when: RuleConstraints | undefined,
): boolean => {
  if (!when) return true
  const t = targetOf(s, command)
  if (when.level !== undefined) {
    if (t.topLevel === null) return false
    if ((when.level === "top") !== t.topLevel) return false
  }
  if (when.under !== undefined) {
    switch (when.under) {
      case "existing_goal":
        if (
          command._tag === "CreateItem" &&
          !(command.goalIds ?? []).every((g) => s.goals.some((x) => x.id === g && x.status === "active"))
        )
          return false
        break
      case "own_area":
        if (agent.areaId === null) return false
        if (command._tag === "AddEdge") {
          if (t.areaId !== agent.areaId || t.requestingAreaId !== agent.areaId) return false
        } else if ((t.requestingAreaId ?? t.areaId) !== agent.areaId) return false
        break
      case "own_tree": {
        const top = t.topItemId ? itemOf(s, t.topItemId) : undefined
        if (!top || ownerAgentIdOf(top) !== agent.id) return false
        break
      }
      case "own_item": {
        const itemId = t.itemId
        if (!itemId || !t.itemIsChild) return false
        if (actor._tag === "worker") {
          if (actor.itemId !== itemId) return false
        } else if (ownerAgentIdOf(itemOf(s, itemId)) !== agent.id) return false
        break
      }
    }
  }
  if (when.maxActive !== undefined) {
    const open = command._tag === "CreateItem" ? openPlannableCount(s) : nowCount(s)
    if (open >= when.maxActive) return false
  }
  if (when.maxConcurrent !== undefined && liveWorkerRuns(s, agent.id).length >= when.maxConcurrent)
    return false
  if (when.to !== undefined) {
    const owner = t.toAgentId ? agentOf(s, t.toAgentId) : undefined
    if (owner?.role !== "role") return false
    if (when.to === "own_area_agents" && owner.areaId !== agent.areaId) return false
  }
  if (when.ifWorkDone !== undefined) {
    const workDone = t.topItemId ? workDoneIn(s, t.topItemId) : false
    if (workDone !== when.ifWorkDone) return false
  }
  if (when.maxDepth !== undefined && t.depth !== null && t.depth > when.maxDepth) return false
  return true
}

type LayerVerdict =
  | { readonly _tag: "never"; readonly rule: string; readonly policy: Policy }
  | {
      readonly _tag: "allow" | "propose"
      readonly rule: string
      readonly policy: Policy
      readonly downgraded: boolean
    }

/** One policy layer's say about a capability, or undefined if it doesn't mention it. */
const evaluateLayer = (
  policy: Policy,
  cap: Capability,
  holds: (r: PolicyRule) => boolean,
): LayerVerdict | undefined => {
  const doc = policy.document
  if (doc.never.includes(cap)) return { _tag: "never", rule: `never.${cap}`, policy }
  const proposeRules = doc.propose.filter((r) => r.capability === cap)
  const allowRules = doc.allow.filter((r) => r.capability === cap)
  if (proposeRules.length === 0 && allowRules.length === 0) return undefined
  // within a layer, a matching propose rule beats allow (propose beats allow)
  const p = proposeRules.find(holds)
  if (p) return { _tag: "propose", rule: `propose.${cap}`, policy, downgraded: false }
  const a = allowRules.find(holds)
  if (a) return { _tag: "allow", rule: `allow.${cap}`, policy, downgraded: false }
  // allow rules exist but their constraints fail: downgrade to propose (a person can approve going over a limit)
  if (allowRules.length > 0)
    return { _tag: "propose", rule: `allow.${cap}:constraint`, policy, downgraded: true }
  return { _tag: "propose", rule: `propose.${cap}`, policy, downgraded: false }
}

/** Layers from widest to narrowest: project, the target's area, the agent. */
export const policyLayers = (
  s: ProjectState,
  agent: Agent,
  areaId: Ids["AreaId"] | null,
): ReadonlyArray<Policy> => {
  const ids = [
    s.project?.policyId ?? null,
    areaId ? (areaOf(s, areaId)?.policyId ?? null) : null,
    agent.policyId,
  ]
  const out: Policy[] = []
  for (const id of ids) {
    if (id === null) continue
    const p = s.policies.find((x) => x.id === id)
    if (p && !out.includes(p)) out.push(p)
  }
  return out
}

/**
 * Effective verdict for one capability: `never` in any layer wins; otherwise the narrowest layer that mentions
 * the capability decides (a narrower layer may tighten or loosen); unmentioned → `propose`.
 */
export const effectiveVerdict = (
  s: ProjectState,
  agent: Agent,
  areaId: Ids["AreaId"] | null,
  cap: Capability,
  holds: (r: PolicyRule) => boolean,
): LayerVerdict | { readonly _tag: "default" } => {
  if (FORCED_NEVER.includes(cap)) {
    const p = policyLayers(s, agent, areaId)[0]
    return p ? { _tag: "never", rule: `forced.${cap}`, policy: p } : { _tag: "default" }
  }
  const layers = policyLayers(s, agent, areaId).map((p) => evaluateLayer(p, cap, holds))
  const never = layers.find((l) => l?._tag === "never")
  if (never) return never
  for (let i = layers.length - 1; i >= 0; i--) {
    const l = layers[i]
    if (l) return l
  }
  return { _tag: "default" }
}

const stamp = (
  policy: Policy | null,
  verdict: PolicyStamp["verdict"],
  ruleName: string | null,
): PolicyStamp => ({
  policyId: policy?.id ?? null,
  revision: policy?.revision ?? 0,
  verdict,
  rule: ruleName,
})

export const exemptStamp = (ruleName: string | null = null): PolicyStamp => stamp(null, "exempt", ruleName)

/** The person a proposing agent or worker works for. */
export const ownerOfActor = (s: ProjectState, actor: Actor): UserId | null => {
  switch (actor._tag) {
    case "user":
      return actor.userId
    case "agent":
    case "worker":
      return actor.onBehalfOf
    default:
      return s.project?.ownerId ?? null
  }
}

/** The agent whose policy governs an actor: itself, or the item's owner agent for a worker. */
export const policyAgentOf = (s: ProjectState, actor: Actor): Agent | undefined =>
  actor._tag === "agent"
    ? agentOf(s, actor.agentId)
    : actor._tag === "worker"
      ? agentOf(s, actor.agentId)
      : undefined

/**
 * evaluatePolicy(state, actor, command) for one graph change (§8.1 steps 3–5). Actor-kind closure, ownership and
 * holds are checked by authorize/validate; taint only affects external effects and memory, never graph edits.
 */
export const evaluatePolicy = (s: ProjectState, actor: Actor, command: GraphCommand): PolicyVerdict => {
  const cap = capabilityOf(command, s)
  if (actor._tag === "user") return { _tag: "exempt", stamp: exemptStamp(null) }
  if (actor._tag === "system" || actor._tag === "runner")
    return { _tag: "never", rule: `actor.${actor._tag}` }
  const agent = policyAgentOf(s, actor)
  if (!agent) return { _tag: "never", rule: "unknown_agent" }
  if (FORCED_NEVER.includes(cap)) return { _tag: "never", rule: `forced.${cap}` }
  // DecideRequest belongs to the receiving area's agent (§5.3), not to a policy capability
  if (command._tag === "DecideRequest") {
    const t = itemOf(s, command.itemId)
    const area = t?.areaId ? areaOf(s, t.areaId) : undefined
    return area?.intakeAgentId === agent.id
      ? { _tag: "allow", stamp: stamp(null, "allow", "intake_agent") }
      : { _tag: "never", rule: "intake_agent_only" }
  }
  // Decision 0097: a KPI's recipe is working knowledge (how to fetch the number), kept by whoever records the KPI (the
  // lead or the agent of its area, decision 0045). A patch that only sets or clears the recipe is theirs without a card.
  if (
    command._tag === "UpdateKpi" &&
    actor._tag === "agent" &&
    Object.entries(command.patch).every(([key, v]) => key === "recipe" || v === undefined)
  ) {
    const k = kpiOf(s, command.kpiId)
    if (k && (leadOf(s)?.id === agent.id || (k.areaId !== null && agent.areaId === k.areaId)))
      return { _tag: "allow", stamp: stamp(null, "allow", "kpi_recipe") }
  }
  // An agent's capture (a top-level item into triage) creates no work: triage is never dispatched, and accepting it
  // (MoveItem, AssignItem) is checked as usual. v1 checked no capability for a capture either (CaptureIntake), so a
  // role agent's capture is not a proposal to a person (integrator, work-model cutover; decision 0037).
  if (command._tag === "CreateItem" && command.status === "triage" && (command.parentId ?? null) === null)
    return { _tag: "allow", stamp: stamp(null, "allow", "capture") }
  // Wave C (decision 0071, spec §7.2): Run now on a series the clock noted due (NoteSeriesDue) is the owner agent's to
  // press: the clock woke it for exactly this. Any other RunOccurrence by an agent is `schedule_work` as before.
  if (command._tag === "RunOccurrence") {
    const t = itemOf(s, command.itemId)
    const period = t ? s.seriesDue?.[t.id] : undefined
    if (
      t?.owner?._tag === "agent" &&
      t.owner.agentId === agent.id &&
      period !== undefined &&
      !s.items.some((x) => x.parentId === t.id && x.intentKey.startsWith(`occ-${period}-`))
    )
      return { _tag: "allow", stamp: stamp(null, "allow", "series_due") }
  }
  // A role agent may file a cross-area request without create_item in the receiving area (§7.2): its own area decides.
  const t = targetOf(s, command)
  const areaId = command._tag === "CreateItem" ? t.requestingAreaId : t.areaId
  const holds = (r: PolicyRule) => constraintsHold(s, actor, agent, command, r.when)
  const primary = verdictOf(s, agent, areaId, cap, holds)
  // decision 0084 (§1.5), the Ready gate: a change that makes work Ready is also judged as `ready_item`, but only when
  // some layer mentions it (projects whose policies never name it behave as before); the stricter verdict wins
  if (
    primary._tag !== "never" &&
    makesReady(s, command) !== null &&
    policyLayers(s, agent, areaId).some((p) => mentions(p.document, "ready_item"))
  ) {
    const ready = verdictOf(s, agent, areaId, "ready_item", holds)
    if (STRICTNESS[ready._tag] > STRICTNESS[primary._tag]) return ready
  }
  return primary
}

/**
 * Decision 0138 (rule-made cards): an agent's change that a person-made rule turns into a card (the brief, checks a
 * person froze) applies when the agent's policy grants it explicitly: `allow revise_brief`, or `allow revise_checks`
 * with `when.frozen: true`. Unmentioned stays a card, so existing policies behave as before.
 */
export const policyGrants = (s: ProjectState, actor: Actor, command: GraphCommand): boolean => {
  if (actor._tag !== "agent" && actor._tag !== "worker") return false
  const agent = policyAgentOf(s, actor)
  if (!agent) return false
  if (command._tag === "ReviseBrief") return evaluatePolicy(s, actor, command)._tag === "allow"
  if (command._tag !== "ReviseChecks") return false
  const holds = (r: PolicyRule) =>
    r.when?.frozen === true && constraintsHold(s, actor, agent, command, r.when)
  return effectiveVerdict(s, agent, targetOf(s, command).areaId, "revise_checks", holds)._tag === "allow"
}

const STRICTNESS: Readonly<Record<PolicyVerdict["_tag"], number>> = {
  exempt: 0,
  allow: 1,
  propose: 2,
  never: 3,
}

/** One capability's verdict for an agent (the layers, then the unmentioned default). */
const verdictOf = (
  s: ProjectState,
  agent: Agent,
  areaId: Ids["AreaId"] | null,
  cap: Capability,
  holds: (r: PolicyRule) => boolean,
): PolicyVerdict => {
  const v = effectiveVerdict(s, agent, areaId, cap, holds)
  const approver: Approver = { _tag: "human", userId: agent.ownerId }
  switch (v._tag) {
    case "never":
      return { _tag: "never", rule: v.rule }
    case "allow":
      return { _tag: "allow", stamp: stamp(v.policy, "allow", v.rule) }
    case "propose":
      return {
        _tag: "propose",
        stamp: stamp(v.policy, "propose", v.rule),
        approver,
        why: v.downgraded ? `${cap}: over the policy's limit` : `${cap} needs approval`,
        asked: { rule: v.downgraded ? "policy_limit" : "policy", capability: cap },
      }
    case "default":
      if (cap === "schedule_work" || cap === "coordinate_projects")
        return { _tag: "allow", stamp: stamp(null, "allow", "default.schedule_work") }
      return {
        _tag: "propose",
        stamp: stamp(null, "propose", `default.${cap}`),
        approver,
        why: `${cap} is not allowed by any policy`,
        asked: { rule: "no_policy", capability: cap },
      }
  }
}

/** Is a non-graph capability (spawn_workers, propose_memory, curate_memory) usable by this agent? */
export const capabilityVerdict = (
  s: ProjectState,
  agent: Agent,
  cap: Capability,
  areaId: Ids["AreaId"] | null = agent.areaId,
): "allow" | "propose" | "never" => {
  const v = effectiveVerdict(s, agent, areaId, cap, (r) => {
    if (!r.when) return true
    if (r.when.maxConcurrent !== undefined && liveWorkerRuns(s, agent.id).length >= r.when.maxConcurrent)
      return false
    return true
  })
  return v._tag === "default"
    ? cap === "schedule_work" || cap === "coordinate_projects"
      ? "allow"
      : "propose"
    : v._tag
}

/** The live-worker limit for an owner agent (readiness `at_capacity`); null = no limit stated. */
export const workerLimitOf = (s: ProjectState, agent: Agent): number | null => {
  const layers = policyLayers(s, agent, agent.areaId)
  for (let i = layers.length - 1; i >= 0; i--) {
    const doc = must(layers[i], "policy layer").document
    if (doc.never.includes("spawn_workers")) return 0
    const r =
      doc.allow.find((x) => x.capability === "spawn_workers") ??
      doc.propose.find((x) => x.capability === "spawn_workers")
    if (r) return r.when?.maxConcurrent ?? null
  }
  return null
}

/** Does a policy document mention a capability (in allow, propose or never)? */
const mentions = (doc: PolicyDocument, cap: Capability): boolean =>
  doc.never.includes(cap) ||
  doc.allow.some((r) => r.capability === cap) ||
  doc.propose.some((r) => r.capability === cap)

/**
 * `like`'s rules copied to `add` (constraints kept), or null when the document doesn't mention `like` or already
 * mentions `add`. The handler of a stored `UpgradePolicies` (decision 0032); the v2 Project DO never issues it: the
 * 0032 backfill is a read-time rule on v1 documents now (decision 0036).
 */
export const upgradedDocument = (
  doc: PolicyDocument,
  add: Capability,
  like: Capability,
): PolicyDocument | null => {
  if (!mentions(doc, like) || mentions(doc, add)) return null
  const copy = (rules: ReadonlyArray<PolicyRule>): ReadonlyArray<PolicyRule> =>
    rules.flatMap((r) =>
      r.capability === like ? [r, r.when ? { capability: add, when: r.when } : { capability: add }] : [r],
    )
  return {
    allow: copy(doc.allow),
    propose: copy(doc.propose),
    never: doc.never.includes(like) ? [...doc.never, add] : doc.never,
  }
}

/**
 * Decision 0084 (§7.2), the Ready switch on the agent page: `ready_item` as one plain rule in `propose` ("needs
 * you") or `allow` ("on her own"), every other `ready_item` rule (constrained ones and a `never`) removed. Other
 * capabilities are left alone.
 */
export const withReadyItem = (doc: PolicyDocument, to: "allow" | "propose"): PolicyDocument => {
  const others = (rules: ReadonlyArray<PolicyRule>) => rules.filter((r) => r.capability !== "ready_item")
  const rule: PolicyRule = { capability: "ready_item" }
  return {
    allow: to === "allow" ? [...others(doc.allow), rule] : others(doc.allow),
    propose: to === "propose" ? [...others(doc.propose), rule] : others(doc.propose),
    never: doc.never.filter((c) => c !== "ready_item"),
  }
}

/** A policy document may never move a forced capability out of `never` for agents (§7.1 Policies). */
export const documentViolations = (doc: PolicyDocument): ReadonlyArray<Capability> =>
  FORCED_NEVER.filter(
    (c) => doc.allow.some((r) => r.capability === c) || doc.propose.some((r) => r.capability === c),
  )

/**
 * Tools offered to a run = actor kind ∩ effective policy: a capability that is `never` has no tool (§8.6).
 */
export const toolsFor = (s: ProjectState, actor: Actor, _runTaint: Taint): ReadonlyArray<string> => {
  let kind: "agent" | "worker" | "validator" | "mayor"
  let agent: Agent | undefined
  if (actor._tag === "agent") {
    if (s.mayor?.agentId === actor.agentId) kind = "mayor"
    else kind = "agent"
    agent = agentOf(s, actor.agentId)
  } else if (actor._tag === "worker") {
    kind = runOf(s, actor.runId)?.kind === "validator" ? "validator" : "worker"
    agent = agentOf(s, actor.agentId)
  } else return []
  const item = actor._tag === "worker" ? itemOf(s, actor.itemId) : undefined
  const out: string[] = []
  for (const [name, tool] of Object.entries(GraphTools)) {
    if (!(tool.actors as ReadonlyArray<string>).includes(kind)) continue
    if (tool.capability !== null && kind !== "mayor") {
      if (!agent) continue
      if (capabilityVerdict(s, agent, tool.capability, item?.areaId ?? agent.areaId) === "never") continue
    }
    out.push(name)
  }
  return out
}
